import { ChevronRightIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { EmailEntityId, relativeTimeAgo, UserMailboxMessageId } from "@sp-crm/core";
import React from "react";
import { AdvancedSearchEntityType } from "../../generated/graphql";
import { isoDateStringToHumanReadable } from "../../util/date/locale-date-format";
import { getSanitizedMarkUpFromReceivedEmailBody } from "../../util/email";
import { InlineBanner } from "../ui/inline-banner";
import { SecondaryCompactButton } from "../ui/secondary-button";
import { Spinner } from "../ui/spinner";
import { EmailBodyWithQuoteToggle } from "./email-body-with-quote-toggle";
import { MessageAttachmentList } from "./message-attachment-list";
import { messageBodyErrorText } from "./message-body-error";
import { ReceivedMessage } from "./received-message-detail";
import { formatContactDisplay } from "./unified-message";
import { MessageBodyData, useReceivedMessageBodyQuery } from "./use-message-body";

interface Props {
    message: ReceivedMessage;
    entityId?: EmailEntityId;
    entityType?: AdvancedSearchEntityType;
    onCollapse: (() => void) | undefined;
    headerActions?: React.ReactNode;
}

const BodyFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="pt-2 pb-3 pl-11 pr-5">{children}</div>
);

const ReceivedMessageBody: React.FC<{
    message: ReceivedMessage;
    entityId?: EmailEntityId;
    entityType?: AdvancedSearchEntityType;
}> = ({ message, entityId, entityType }) => {
    const query = useReceivedMessageBodyQuery(
        entityId,
        message.id as UserMailboxMessageId,
    );

    if (query.isLoading) {
        return <Spinner />;
    }

    if (query.isError) {
        return (
            <InlineBanner type="error">
                <div className="flex items-center justify-between gap-3">
                    <span>{messageBodyErrorText(query.error)}</span>
                    <SecondaryCompactButton
                        type="button"
                        onClick={() => query.refetch()}
                        disabled={query.isFetching}>
                        Try again
                    </SecondaryCompactButton>
                </div>
            </InlineBanner>
        );
    }

    const body = query.data as MessageBodyData | undefined;
    const attachments = body?.attachments ?? [];
    const showAttachments = entityId && entityType && attachments.length > 0;
    const attachmentList = showAttachments ? (
        <MessageAttachmentList
            messageId={message.id as UserMailboxMessageId}
            entityId={entityId}
            entityType={entityType}
            attachments={attachments}
        />
    ) : null;

    if (body?.html) {
        return (
            <>
                <EmailBodyWithQuoteToggle
                    kind="html"
                    html={body.html}
                    sanitize={getSanitizedMarkUpFromReceivedEmailBody}
                />
                {attachmentList}
            </>
        );
    }
    if (body?.plain) {
        return (
            <>
                <EmailBodyWithQuoteToggle kind="plain" plain={body.plain} />
                {attachmentList}
            </>
        );
    }
    if (message.bodyPreview) {
        return (
            <>
                <EmailBodyWithQuoteToggle kind="plain" plain={message.bodyPreview} />
                {attachmentList}
            </>
        );
    }
    return (
        <>
            <InlineBanner type="warning">
                This message has no readable body. The original may be empty or its
                content was not retrieved during sync.
            </InlineBanner>
            {attachmentList}
        </>
    );
};

export const ReceivedMessageInlineDetail: React.FC<Props> = props => {
    const { message, entityId, entityType, onCollapse, headerActions } = props;

    const fromDisplay = formatContactDisplay(
        message.from?.name,
        message.from?.email,
        message.from?.resolvedName,
    );

    const toRecipients = message.to
        .map(r =>
            formatContactDisplay(r.contact.name, r.contact.email, r.contact.resolvedName),
        )
        .join(", ");

    const ccRecipients = message.cc
        .map(r =>
            formatContactDisplay(r.contact.name, r.contact.email, r.contact.resolvedName),
        )
        .join(", ");

    const bccRecipients = message.bcc
        .map(r =>
            formatContactDisplay(r.contact.name, r.contact.email, r.contact.resolvedName),
        )
        .join(", ");

    const timestamp = new Date(message.receivedTimestamp);

    return (
        <div className="border-b border-gray-200">
            <div
                className={`flex items-start gap-3 px-5 py-2 ${
                    onCollapse ? "hover:bg-gray-50 cursor-pointer" : ""
                }`}
                onClick={onCollapse}>
                {onCollapse ? (
                    <ChevronRightIcon
                        className="h-3 w-3 text-gray-400 flex-shrink-0 mt-1 rotate-90"
                        aria-hidden="true"
                    />
                ) : null}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm text-gray-900">
                            {fromDisplay}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {message.attachmentCount > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                                    <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" />
                                    {message.attachmentCount}
                                </span>
                            )}
                            <span className="text-xs text-gray-500">
                                {relativeTimeAgo(timestamp)}
                            </span>
                            {headerActions}
                        </div>
                    </div>
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">To:</span> {toRecipients}
                    </div>
                    {ccRecipients ? (
                        <div className="text-sm text-gray-500">
                            <span className="text-gray-400">Cc:</span> {ccRecipients}
                        </div>
                    ) : null}
                    {bccRecipients ? (
                        <div className="text-sm text-gray-500">
                            <span className="text-gray-400">Bcc:</span> {bccRecipients}
                        </div>
                    ) : null}
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">Received:</span>{" "}
                        {isoDateStringToHumanReadable(message.receivedTimestamp)}
                    </div>
                </div>
            </div>
            <BodyFrame>
                <ReceivedMessageBody
                    message={message}
                    entityId={entityId}
                    entityType={entityType}
                />
            </BodyFrame>
        </div>
    );
};
